import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Play } from 'lucide-react';

export default function BlogCard({ post }) {
  // Safe date logic
  const date = post.published_at 
    ? new Date(Number(post.published_at) * 1000).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    : new Date().toLocaleDateString();

  // Thumbnail fallback - avoid placeholder URLs if possible
  const validThumb = (url) => {
    if (!url) return false;
    if (url.includes('placehold.co') || url.includes('example.com')) return false;
    return true;
  };

  const thumbnail = validThumb(post.thumbnail_url) 
    ? post.thumbnail_url 
    : 'https://images.unsplash.com/photo-1562322140-8baeececf3df?auto=format&fit=crop&q=80&w=800'; // Aesthetic fallback

  return (
    <div className="group bg-[#15151e] border border-white/5 rounded-2xl overflow-hidden hover:border-fuchsia-500/30 transition-all duration-300 hover:shadow-lg hover:shadow-fuchsia-500/5 h-full flex flex-col">
      {/* Thumbnail */}
      <Link to={`/blog/${post.slug}`} className="relative aspect-[16/9] block overflow-hidden bg-black/40">
        <img 
          src={thumbnail}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => {
             e.target.onerror = null;
             e.target.src = 'https://images.unsplash.com/photo-1562322140-8baeececf3df?auto=format&fit=crop&q=80&w=800';
          }}
        />
        {/* Video Overlay */}
        {post.type === 'video' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur border border-white/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Play size={20} className="text-white fill-white ml-1" />
            </div>
          </div>
        )}
      </Link>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center justify-between text-xs text-white/40 mb-3">
           <div className="flex items-center gap-2">
             <span className={`px-2 py-0.5 rounded uppercase font-bold tracking-wider text-[10px] border ${
               post.type === 'video' 
                 ? 'bg-red-500/10 text-red-400 border-red-500/20' 
                 : 'bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/20'
             }`}>
               {post.type === 'video' ? 'Video' : 'Article'}
             </span>
             <span className="flex items-center gap-1">
               <span className="font-mono">{post.reading_time_minutes || 5}</span> min read
             </span>
           </div>
           
           {(post.likes > 0 || post.dislikes > 0) && (
              <span className="flex items-center gap-1 opacity-60" title="Reactions">
                 <span className="text-xs">👍</span> {post.likes || 0}
              </span>
           )}
        </div>

        <Link to={`/blog/${post.slug}`} className="block mb-2 group-hover:text-fuchsia-400 transition-colors">
          <h3 className="text-xl font-bold text-white line-clamp-2 leading-tight">
            {post.title}
          </h3>
        </Link>
        
        <p className="text-white/60 text-sm line-clamp-3 mb-4 flex-1 leading-relaxed">
          {post.excerpt || post.meta_description || "Explore the latest trends and guides in AI hairstyle transformations."}
        </p>

        <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between text-xs text-white/40 font-medium uppercase tracking-wider">
          <div className="flex items-center gap-1.5">
            <User size={12} />
            <span className="truncate max-w-[120px]">{post.author_name || 'AI Hairstyle Studio'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar size={12} />
            <span>{date}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
